import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import { Typography, Divider, Stack, ListItemButton } from "@mui/material"
import {
    bindHover,
    bindPopover,
    usePopupState,
} from "material-ui-popup-state/hooks"
import HoverPopover from "material-ui-popup-state/HoverPopover"
import { ReactElement } from "react"

import { NAV_ICON_SX } from "./navBarWrapper"
import NavButtonHorizontal from "./navButtons/navButtonHorizontal"
import NavButtonVerticalExpanded from "./navButtons/navButtonVerticalExpanded"
import { useScreenSize } from "../../../hooks/useScreenSize"
import { BORDER_RADIUS, GRID_SPACING } from "../../../themes/defaultThemeParams"
import { navOptionsByGroup, NavOptionType } from "../navOptions"

const NUM_HORIZONTAL_NAV_OPTIONS_XL = 7
const NUM_HORIZONTAL_NAV_OPTIONS_MD = 4

type NavBarHorizontalProps = {
    groups: string[]
    children: ReactElement
}

/**
 * Returns navigation bar in horizontal, lengthwise mode. Wraps the content box
 * for the application, via the `children` component
 *
 * @example
 * <NavBarHorizontal groups={groups}>
 *     <Content />
 * </NavBarHorizontal>
 */
const NavBarHorizontal = ({
    groups,
    children,
}: NavBarHorizontalProps): ReactElement => {
    const popupState = usePopupState({
        variant: "popover",
        popupId: "demoPopover",
    })
    const { isXl } = useScreenSize()
    const numNavOptions = isXl
        ? NUM_HORIZONTAL_NAV_OPTIONS_XL
        : NUM_HORIZONTAL_NAV_OPTIONS_MD
    return (
        <>
            <Divider />
            <Stack
                direction="row"
                padding={1}
                spacing={GRID_SPACING - 1}
                sx={{
                    border: "#442ddd",
                    backgroundColor: "background.frameBackground",
                    backgroundImage: "none",
                }}
            >
                <>
                    {navOptionsByGroup(groups)
                        .options.slice(0, numNavOptions)
                        .map((option: NavOptionType) => (
                            <NavButtonHorizontal
                                key={`NavHorizontal${option.group}${option.name}`}
                                title={option.name}
                                route={option.route}
                                external={option?.external}
                                icon={
                                    <option.icon
                                        sx={{ ...NAV_ICON_SX, mr: 2 }}
                                    />
                                }
                            />
                        ))}
                    {navOptionsByGroup(groups).options.length >
                        numNavOptions && (
                        <ListItemButton
                            sx={{
                                textTransform: "none",
                                borderRadius: BORDER_RADIUS,
                                paddingX: 2,
                                flexGrow: 0,
                            }}
                            color="primary"
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...bindHover(popupState)}
                        >
                            <Typography
                                variant="body1"
                                color="text.primary"
                                fontWeight="normal"
                            >
                                More Options
                            </Typography>
                            {popupState.isOpen ? (
                                <KeyboardArrowDownIcon sx={NAV_ICON_SX} />
                            ) : (
                                <KeyboardArrowRightIcon sx={NAV_ICON_SX} />
                            )}
                            <HoverPopover
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...bindPopover(popupState)}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "center",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "center",
                                }}
                            >
                                <>
                                    {navOptionsByGroup(groups)
                                        .options.slice(numNavOptions)
                                        .map((option: NavOptionType) => (
                                            <NavButtonVerticalExpanded
                                                key={`NavPopper${option.group}${option.name}`}
                                                title={option.name}
                                                route={option.route}
                                                external={option?.external}
                                                icon={
                                                    <option.icon
                                                        sx={{
                                                            ...NAV_ICON_SX,
                                                            mr: 2,
                                                        }}
                                                    />
                                                }
                                            />
                                        ))}
                                </>
                            </HoverPopover>
                        </ListItemButton>
                    )}
                </>
            </Stack>
            {children}
        </>
    )
}

export default NavBarHorizontal
